/**
 * ProfileMenu — merged profile button + workspace switcher + user actions.
 *
 * Replaces the separate UserMenu (top-right) and WorkspaceSwitcher (top-left pill)
 * with a SINGLE profile button in the top-right that opens a unified menu
 * matching option-a.html § .menu / .profbtn.
 *
 * Menu structure (exact mockup order):
 *   "Workspace" section header (Space Mono eyebrow)
 *   [workspace items — current ticked, accent-l bg]
 *   separator
 *   My data, My activity, Help (mailto)
 *   separator
 *   Sign out
 *
 * Workspace logic: reuses Track A WorkspaceSwitcher endpoint wiring:
 *   GET  /api/me/workspaces      — list personal + companies + admin
 *   POST /api/me/active-context  — persist active context before navigate
 *
 * Classes from shell.css (app-profbtn, app-av, app-menu, app-wi, app-mi, …).
 * No `any` (Inv 13). No console.log (Inv 12). Nav links use <Link> (Inv 14).
 */

import {
  useRef,
  useState,
  useEffect,
  type ReactElement,
  type MouseEvent,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '../../state/auth/api.ts';
import { useWorkspaces, postActiveContext, type Workspace } from './useWorkspaces.ts';

const SUPPORT_EMAIL = 'support@bimassess.com';

/** Initials extracted from a display name (max 2 chars, uppercase). */
function initials(name: string): string {
  const result = name
    .trim()
    .split(/\s+/)
    .map((n) => n[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return result || '?';
}

/** Icon background + foreground colours per workspace type (matches mockup). */
function wicStyle(ws: Workspace, isActive: boolean): { background: string; color: string } {
  if (isActive) return { background: 'var(--accent)', color: 'var(--bg)' };
  if (ws.type === 'personal') return { background: 'var(--accent-l)', color: 'var(--accent)' };
  if (ws.type === 'admin') return { background: 'var(--bg-warm)', color: 'var(--ink)' };
  // company (non-active)
  return { background: 'var(--accent-l)', color: 'var(--accent)' };
}

/** Single-letter abbreviation for workspace icon. */
function wicLabel(ws: Workspace): string {
  return ws.name.trim()[0]?.toUpperCase() ?? '?';
}

/** Role label shown at right of workspace row (matches mockup .role). */
function roleLabel(ws: Workspace): string {
  if (ws.type === 'admin') return 'admin';
  if (ws.type === 'personal') return 'you';
  return ws.role ?? '';
}

export interface ProfileMenuProps {
  userName: string;
  userEmail: string;
  onSignOut: () => void;
  /** Current pathname — used to determine active workspace. */
  currentPathname: string;
}

export function ProfileMenu({
  userName,
  onSignOut,
  currentPathname,
}: ProfileMenuProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { workspaces } = useWorkspaces();

  // Derive active workspace id from URL (mirrors WorkspaceSwitcher logic)
  function activeWorkspaceId(): string {
    if (currentPathname.startsWith('/admin')) return 'admin';
    const match = currentPathname.match(/^\/company\/([^/]+)/);
    if (match) return `company-${match[1] ?? ''}`;
    return 'personal';
  }

  const activeId = activeWorkspaceId();

  const handleButtonClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const closeMenu = (): void => {
    setIsOpen(false);
  };

  /** Close + return focus to the trigger (keyboard / scrim dismissal). */
  const closeAndReturnFocus = (): void => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  // Close on click outside. The panel + scrim are portaled to <body> (so the
  // bottom-sheet's position:fixed escapes the top bar's backdrop-filter
  // containing block), so they're NOT DOM descendants of menuRef — check the
  // trigger and the portaled panel explicitly. The scrim has its own onClick.
  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (evt: Event): void => {
      const target = evt.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('click', handleOutside as EventListener, { once: true });
    return () => document.removeEventListener('click', handleOutside as EventListener);
  }, [isOpen]);

  // Esc closes (matches Shell drawer behaviour); move focus into the menu on
  // open so the bottom-sheet is keyboard-reachable and the focus trap has an
  // anchor inside the panel.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeAndReturnFocus();
      }
    };
    document.addEventListener('keydown', onKey);
    const first = dropdownRef.current?.querySelector<HTMLElement>(
      '[role="menuitem"]',
    );
    first?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  /** Focus trap: keep Tab focus cycling within the open menu panel. */
  const handleMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'Tab') return;
    const panel = dropdownRef.current;
    if (!panel) return;
    const focusables = Array.from(
      panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first?.focus();
    }
  };

  const handleWorkspaceSelect = (ws: Workspace): void => {
    closeMenu();
    postActiveContext(ws, () => {
      navigate(ws.rootRoute);
    });
  };

  const handleSignOut = async (): Promise<void> => {
    // Best-effort server-side sign-out (clears DB session). Always redirect
    // locally regardless of the outcome — the user must be able to sign out
    // even when the backend is down or the session is already invalid.
    try {
      await authApi.signOut();
    } catch {
      // Ignore — proceed with local redirect.
    }
    onSignOut();
  };

  const userInitials = initials(userName);

  return (
    <div ref={menuRef} className="app-prof" data-testid="profile-menu">
      <button
        ref={triggerRef}
        type="button"
        className="app-profbtn"
        onClick={handleButtonClick}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={`Profile menu for ${userName}`}
        data-testid="profile-menu-trigger"
      >
        <span className="app-av" aria-hidden="true">{userInitials}</span>
        {userName}
        <span className="app-cv" aria-hidden="true">▾</span>
      </button>

      {isOpen && createPortal(
        <>
          {/* Scrim — mobile only (bottom-sheet backdrop); tap closes.
              Hidden on desktop via shell.css. Mirrors the drawer scrim.

              Portaled to <body> (with the panel) so the bottom-sheet's
              position:fixed resolves against the viewport. The top bar
              (.app-top) sets backdrop-filter, which would otherwise make it
              the containing block for fixed descendants — collapsing the sheet
              into the 52px bar. The drawer scrim works because it already
              renders outside .app-top; this matches that placement. */}
          <div
            className="app-menu-scrim"
            aria-hidden="true"
            onClick={closeAndReturnFocus}
            data-testid="profile-menu-scrim"
          />
          <div
            ref={dropdownRef}
            className="app-menu"
            role="menu"
            aria-label="Profile and workspace menu"
            onKeyDown={handleMenuKeyDown}
            data-testid="profile-menu-dropdown"
          >
            {/* Grab handle — only visible in the mobile bottom-sheet. */}
            <div className="app-sheet-handle" aria-hidden="true" />

            {/* Workspace section */}
            <div className="app-menu-h">Workspace</div>
          {workspaces.map((ws) => {
            const active = ws.id === activeId;
            const style = wicStyle(ws, active);
            return (
              <button
                key={ws.id}
                type="button"
                className={`app-wi${active ? ' cur' : ''}`}
                role="menuitem"
                onClick={() => handleWorkspaceSelect(ws)}
                data-testid="workspace-menu-item"
                data-workspace-id={ws.id}
              >
                <span className="app-wic" style={style}>{wicLabel(ws)}</span>
                <span className="app-wi-name">{ws.name}</span>
                {active
                  ? <span className="app-tick" aria-label="active workspace">✓</span>
                  : <span className="app-role">{roleLabel(ws)}</span>
                }
              </button>
            );
          })}

          <div className="app-sep" />

          {/* User actions */}
          <Link
            to="/me/data"
            className="app-mi"
            role="menuitem"
            onClick={closeMenu}
          >
            My data
          </Link>
          <Link
            to="/me/activity"
            className="app-mi"
            role="menuitem"
            onClick={closeMenu}
          >
            My activity
          </Link>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=bimassess%20support%20request`}
            className="app-mi"
            role="menuitem"
            onClick={closeMenu}
          >
            Help
          </a>

          <div className="app-sep" />

          <button
            type="button"
            className="app-mi"
            role="menuitem"
            onClick={handleSignOut}
            data-testid="sign-out-button"
          >
            Sign out
          </button>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
